/**
 * SVG previews.
 *
 * Two different drawings, on purpose:
 *
 *   <BraceletPreview>  — the bracelet AS WORN, lying flat, length left to
 *                        right. This is what the option cards show, because
 *                        "what will it look like" is the question being asked.
 *
 *   <PatternChart>     — the bracelet AS KNOTTED, rows running down the page,
 *                        which is the orientation she works in and the
 *                        orientation every published chart uses.
 *
 * Both are driven by the simulator's actual output. Nothing here invents a
 * colour: if the preview is wrong, the bracelet is wrong, and a test will have
 * caught it first.
 *
 * SVG rather than canvas so that previews survive printing at full resolution.
 */
import { simulateNormal } from '../engine/normal';
import { simulateAlpha } from '../engine/alpha';
import { simulateChain, MOTIF_INFO } from '../engine/chain';
import { KNOT_BEHAVIOUR } from '../engine/knots';
import type { Design, PatternBody, PaletteEntry } from '../engine/types';

const hexOf = (palette: PaletteEntry[], i: number) => palette[i]?.hex ?? '#999999';

export interface PreviewProps {
  design: Design;
  /** Rows to draw. Long patterns are truncated for the card previews. */
  maxRows?: number;
  className?: string;
  /** Height of the drawing in px. Width follows from the aspect ratio. */
  height?: number;
}

/* -------------------------------------------------------------------------- */
/* As worn                                                                    */
/* -------------------------------------------------------------------------- */

export function BraceletPreview({ design, maxRows = 64, height = 72, className }: PreviewProps) {
  const body = design.body;
  if (body.style === 'normal') return <NormalWorn design={design} maxRows={maxRows} height={height} className={className} />;
  if (body.style === 'alpha') return <AlphaWorn design={design} maxRows={maxRows} height={height} className={className} />;
  return <ChainWorn design={design} height={height} className={className} />;
}

function NormalWorn({ design, maxRows, height, className }: Required<Pick<PreviewProps, 'design' | 'maxRows' | 'height'>> & { className?: string }) {
  const body = design.body as Extract<PatternBody, { style: 'normal' }>;
  const sim = simulateNormal(body);
  const rows = sim.rows.slice(0, maxRows);
  const n = sim.strandCount;

  // As worn: rows run left to right, strings run top to bottom.
  const cell = height / n;
  const width = rows.length * cell;

  return (
    <svg
      className={className}
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`${design.name}: ${design.blurb}`}
    >
      <rect x={0} y={0} width={width} height={height} fill="#efe8dd" />
      {rows.map((row, r) =>
        row.knots.map((k) => {
          const cx = (r + 0.5) * cell;
          const cy = (k.leftPosition + 1) * cell;
          const beh = KNOT_BEHAVIOUR[k.knot];
          const dy = beh.shows === 'left' ? -1 : 1;
          return (
            <line
              key={`s${r}-${k.index}`}
              x1={cx - cell * 0.55}
              y1={cy - dy * cell * 0.55}
              x2={cx + cell * 0.55}
              y2={cy + dy * cell * 0.55}
              stroke={hexOf(design.palette, k.color)}
              strokeWidth={cell * 1.08}
              strokeLinecap="round"
            />
          );
        }),
      )}
    </svg>
  );
}

function AlphaWorn({ design, maxRows, height, className }: Required<Pick<PreviewProps, 'design' | 'maxRows' | 'height'>> & { className?: string }) {
  const body = design.body as Extract<PatternBody, { style: 'alpha' }>;
  const rows = body.grid.slice(0, maxRows);
  const cols = body.baseColors.length;

  // As worn, the knotting rows run left to right, so the word reads normally.
  const cell = height / cols;
  const width = rows.length * cell;

  return (
    <svg
      className={className}
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`${design.name}: ${design.blurb}`}
    >
      <rect x={0} y={0} width={width} height={height} fill="#efe8dd" />
      {rows.map((row, r) =>
        row.map((c, col) => (
          <rect
            key={`${r}-${col}`}
            // Column 0 of the grid is the LEFT edge of the bracelet, which
            // after the quarter turn is the BOTTOM of the readable text.
            x={r * cell}
            y={height - (col + 1) * cell}
            width={cell + 0.4}
            height={cell + 0.4}
            fill={hexOf(design.palette, c)}
          />
        )),
      )}
    </svg>
  );
}

function ChainWorn({ design, height, className }: Required<Pick<PreviewProps, 'design' | 'height'>> & { className?: string }) {
  const body = design.body as Extract<PatternBody, { style: 'chain' }>;
  const sim = simulateChain(body);
  const unit = height / 3;

  // Lay the modules out along the length.
  const spans = sim.modules.map((m) =>
    m.module.kind === 'chain' ? Math.max(1, m.module.knots) * unit * 0.34 : unit * 2.1,
  );
  const width = spans.reduce((a, b) => a + b, 0);
  const midY = height / 2;

  const chainColor = hexOf(design.palette, body.strandColors[0]!);
  let x = 0;

  return (
    <svg
      className={className}
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`${design.name}: ${design.blurb}`}
    >
      <rect x={0} y={0} width={width} height={height} fill="#efe8dd" />
      {sim.modules.map((m, i) => {
        const w = spans[i]!;
        const x0 = x;
        x += w;
        const mod = m.module;
        if (mod.kind === 'chain') {
          const dots = Math.max(1, mod.knots);
          return (
            <g key={i}>
              <line x1={x0} y1={midY} x2={x0 + w} y2={midY} stroke={chainColor} strokeWidth={unit * 0.42} strokeLinecap="round" />
              {Array.from({ length: dots }, (_, j) => (
                <circle key={j} cx={x0 + (j + 0.5) * (w / dots)} cy={midY} r={unit * 0.16} fill="#00000018" />
              ))}
            </g>
          );
        }
        const motif = mod.motif;
        const info = MOTIF_INFO[motif];
        const petals = mod.rounds * info.petalsPerRound;
        const petalColor = hexOf(design.palette, body.strandColors[2]!);
        const centreColor = hexOf(design.palette, body.strandColors[1]!);
        const cx = x0 + w / 2;
        return (
          <g key={i}>
            <line x1={x0} y1={midY} x2={x0 + w} y2={midY} stroke={chainColor} strokeWidth={unit * 0.3} />
            {motif === 'heart' ? (
              <path
                d={heartPath(cx, midY, unit * 0.95)}
                fill={petalColor}
              />
            ) : (
              Array.from({ length: Math.max(3, petals) }, (_, p) => {
                const a = (p / Math.max(3, petals)) * Math.PI * 2 - Math.PI / 2;
                return (
                  <circle
                    key={p}
                    cx={cx + Math.cos(a) * unit * 0.62}
                    cy={midY + Math.sin(a) * unit * 0.62}
                    r={unit * 0.42}
                    fill={petalColor}
                  />
                );
              })
            )}
            {motif !== 'heart' && <circle cx={cx} cy={midY} r={unit * 0.4} fill={centreColor} />}
          </g>
        );
      })}
    </svg>
  );
}

function heartPath(cx: number, cy: number, r: number): string {
  return [
    `M ${cx} ${cy + r * 0.85}`,
    `C ${cx - r * 1.5} ${cy - r * 0.1} ${cx - r * 0.55} ${cy - r * 1.15} ${cx} ${cy - r * 0.35}`,
    `C ${cx + r * 0.55} ${cy - r * 1.15} ${cx + r * 1.5} ${cy - r * 0.1} ${cx} ${cy + r * 0.85}`,
    'Z',
  ].join(' ');
}

/* -------------------------------------------------------------------------- */
/* As knotted                                                                 */
/* -------------------------------------------------------------------------- */

export interface ChartProps {
  design: Design;
  /** 1-based step number to highlight, for the knot-along. */
  highlightStep?: number;
  maxRows?: number;
  cell?: number;
}

/**
 * The working chart: rows down the page, exactly as she will tie them.
 * Optionally highlights one knot, which is what the knot-along uses to answer
 * "where am I".
 */
export function PatternChart({ design, highlightStep, maxRows = 400, cell = 18 }: ChartProps) {
  const body = design.body;
  if (body.style === 'normal') {
    const sim = simulateNormal(body);
    const rows = sim.rows.slice(0, maxRows);
    const width = (sim.strandCount + 1) * cell;
    const height = rows.length * cell + cell;
    let step = 0;
    return (
      <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} className="chart" role="img" aria-label="Knotting chart">
        {/* Setup order along the top. */}
        {sim.initialOrder.map((strand, pos) => (
          <rect key={pos} x={pos * cell + cell * 0.5} y={0} width={cell} height={cell * 0.55} fill={hexOf(design.palette, body.strandColors[strand]!)} />
        ))}
        {rows.map((row, r) =>
          row.knots.map((k) => {
            step += 1;
            const isHere = highlightStep === step;
            const x = (k.leftPosition + 1) * cell;
            const y = r * cell + cell * 1.2;
            const beh = KNOT_BEHAVIOUR[k.knot];
            const dx = beh.shows === 'left' ? -1 : 1;
            return (
              <g key={`${r}-${k.index}`}>
                <line
                  x1={x - dx * cell * 0.5}
                  y1={y - cell * 0.5}
                  x2={x + dx * cell * 0.5}
                  y2={y + cell * 0.5}
                  stroke={hexOf(design.palette, k.color)}
                  strokeWidth={cell * 0.86}
                  strokeLinecap="round"
                />
                <text x={x} y={y + cell * 0.2} textAnchor="middle" fontSize={cell * 0.62} fill="#00000080" fontFamily="monospace">
                  {beh.glyph}
                </text>
                {isHere && <circle cx={x} cy={y} r={cell * 0.72} fill="none" stroke="#111" strokeWidth={2.5} />}
              </g>
            );
          }),
        )}
      </svg>
    );
  }

  if (body.style === 'alpha') {
    const rows = body.grid.slice(0, maxRows);
    const cols = body.baseColors.length;
    const width = cols * cell;
    const height = rows.length * cell;
    const sim = simulateAlpha(body);
    // Step numbers follow the order knots are TIED, which reverses every row,
    // so they cannot be derived from the grid coordinates alone.
    const stepAt = new Map<string, number>();
    let n = 0;
    for (const row of sim.rows) {
      for (const k of row.knots) {
        n += 1;
        stepAt.set(`${k.row}:${k.col}`, n);
      }
    }
    return (
      <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} className="chart" role="img" aria-label="Alpha chart">
        {rows.map((row, r) =>
          row.map((c, col) => {
            const isHere =
              highlightStep !== undefined && stepAt.get(`${r}:${col}`) === highlightStep;
            return (
              <g key={`${r}-${col}`}>
                <rect x={col * cell} y={r * cell} width={cell + 0.3} height={cell + 0.3} fill={hexOf(design.palette, c)} />
                {isHere && <rect x={col * cell} y={r * cell} width={cell} height={cell} fill="none" stroke="#111" strokeWidth={2.5} />}
              </g>
            );
          }),
        )}
        {/* Direction arrows down the left, so she never loses which way a row runs. */}
        {rows.map((_, r) => (
          <text key={`d${r}`} x={2} y={r * cell + cell * 0.8} fontSize={cell * 0.6} fill="#00000055" fontFamily="monospace">
            {r % 2 === (body.firstRowDirection === 'ltr' ? 0 : 1) ? '\u2192' : '\u2190'}
          </text>
        ))}
      </svg>
    );
  }

  // Chain patterns have no grid; the worn view IS the chart.
  return <BraceletPreview design={design} height={90} />;
}

/* -------------------------------------------------------------------------- */

/** A row of colour swatches with DMC codes, used all over the app. */
export function PaletteStrip({ palette, compact }: { palette: PaletteEntry[]; compact?: boolean }) {
  return (
    <ul className={compact ? 'palette palette--compact' : 'palette'}>
      {palette.map((p, i) => (
        <li key={`${p.dmc}-${i}`}>
          <span className="swatch" style={{ background: p.hex }} aria-hidden="true" />
          <span className="swatch-label">
            <strong>{p.dmc}</strong>
            {!compact && <em>{p.name}</em>}
          </span>
        </li>
      ))}
    </ul>
  );
}
